import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./KnnIntroPage.css";

const KnnIntroPage = () => {
  const navigate = useNavigate();

  // Add an effect to manage the body class for a custom background
  useEffect(() => {
    // Add a class to the body element when the component mounts
    document.body.classList.add("knn-page-background");

    // Cleanup function to remove the class when the component unmounts
    return () => {
      document.body.classList.remove("knn-page-background");
    };
  }, []); // The empty dependency array ensures this effect runs only once on mount and cleanup on unmount

  const handleBackClick = () => {
    if (typeof window !== "undefined" && window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="knn-intro-container">
      <div className="knn-back-nav">
        <button
          type="button"
          className="knn-back-button"
          onClick={handleBackClick}
          aria-label="Go back to the previous page"
        >
          <span aria-hidden="true">←</span>
          Back
        </button>
      </div>
      <header className="knn-header">
        <h1>
          Woke AI vs. MAGA: Scalable & Ethical AI in Action with Keisha AI
        </h1>
        <p className="author">
          By: Janga Bussaja (Planetary Chess Inc.; Creator of Keisha AI)
        </p>
      </header>

      <section>
        <h2>Abstract</h2>
        <p>
          In an era of polarized technology ethics, "Woke AI vs. MAGA"
          encapsulates the clash between artificial intelligence committed to
          truth and justice versus reactionary efforts to preserve systemic
          biases. This paper critically examines how white supremacist ideology
          infiltrates mainstream AI systems and how a counter-racist framework
          can overcome these limitations. This paper presents the Keisha News
          Network (KNN)—a production-grade, counter-racist AI stack powered by
          the soWSnewsletter data engine and expressed through The MAGA Files,
          FNS News Decoded, and the Fragile News Decoder—as a concrete, scalable
          alternative to "neutral" AI that habitually obscures white supremacy.
        </p>
        <p>
          In the Trump (2025–) era—marked by day-one pardons/commutations for
          Jan. 6 offenders, federal dismantling of DEI programs, and
          presidential misinformation about the "Epstein files"—the need for
          systems that name power and decode propaganda in real time is urgent.
          We show how KNN operationalizes African-centered scholarship (Welsing,
          Wilson, Ben-Jochannan) into an engineering program that (a) harvests
          longitudinal news and political discourse via soWSnewsletter, (b)
          performs counter-racist content analysis (MAGA Files), and (c)
          live-decodes mainstream segments (FNS).
        </p>
        <p>
          Our findings highlight that conventional large language models (LLMs)
          often exhibit "white fragility" – an avoidance of explicit discussions
          of white supremacy – thereby inadvertently upholding oppressive
          narratives. By contrast, Keisha AI's specialized systems demonstrate a
          deeper, culturally-informed understanding of racism, embedding
          counter-racist principles to challenge entrenched biases. This
          research provides a compelling case for venture investment: truly
          ethical AI systems that can scale globally while courageously
          confronting systemic racism.
        </p>
      </section>

      <section>
        <h2>1. Introduction: AI after January 20, 2025</h2>
        <p>
          The past decade in American politics has been defined by a fierce
          cultural conflict often framed as "woke vs. MAGA". On January 20,
          2025, Donald J. Trump was inaugurated as the 47th U.S. president. His
          administration immediately set the ideological frame for public
          discourse: mass clemency for individuals convicted in connection with
          Jan. 6, and executive actions rolling back DEI across federal domains.
          Under Trump's leadership, the Republican right has cast initiatives on
          diversity, inclusion, and anti-racism as threats to American
          greatness.
        </p>
        <p>
          The MAGA movement's slogan "Make America Great Again" has been
          critically analyzed as "the covert call for colonialism's comeback,"
          laden with subtle echoes of imperialist and segregationist nostalgia.
          In this environment, ethical AI that aspires to neutrality risks
          laundering propaganda. KNN is built to do the opposite: expose it. The
          true bias in AI is not an overabundance of social conscience, but a
          legacy of systemic racism and "white fragility" embedded in mainstream
          models. We posit that ethical AI must explicitly counter these biases
          rather than feign neutrality.
        </p>
        <p>
          Upon regaining the presidency in 2025, Donald Trump wasted no time
          translating his anti-"woke" philosophy into federal policy, unveiling
          an executive order explicitly aimed at "Preventing Woke AI in the
          Federal Government". The order painted DEI as "one of the most
          pervasive and destructive" ideologies afflicting AI, calling it an
          "existential threat to reliable AI".
        </p>
      </section>

      <section>
        <h2>2. System Overview: KNN as a Three-Headed, Data-Driven Platform</h2>
        <p>
          The Keisha News Network (KNN) is a suite of AI tools engineered to
          detect, decode, and dismantle racist patterns in discourse in real
          time. The KNN engine consists of three primary tools, and its
          foundation is a powerful data-gathering agent.
        </p>

        <h3>Data Spine — soWSnewsletter</h3>
        <p>
          The soWSnewsletter is a production Python project that collects,
          normalizes, and enriches articles and segments for downstream
          analysis. This agent, which gathers stories with tags like white
          supremacy, racism, systemic racism, antiracism, MAGA, and Christian
          nationalism, serves as the longitudinal data engine for KNN
          experiments.
        </p>

        <h3>Application Heads — Analysis & Decoding</h3>
        <div className="app-cards">
          <div className="app-card">
            <h4>The MAGA Files (Your AI Truth Machine)</h4>
            <p>
              This is a data repository and analysis engine that archives
              MAGA-related discourse and exposes its underlying racist and
              colonialist subtext. It aggregates rhetoric, tags colonial
              nostalgia, and renders explanatory notes tied to history.
            </p>
            <a
              href="https://maga.talk2keisha.com"
              target="_blank"
              rel="noopener noreferrer"
              className="app-link"
            >
              Go to The MAGA Files
            </a>
          </div>
          <div className="app-card">
            <h4>
              FNS (Fragile News Source) <span className="beta-label">beta</span>
            </h4>
            <p>
              This specialized deployment of KNN is aimed at decoding mainstream
              media news through a counter-racist lens. It ingests transcripts,
              flags omissions, and produces decoded transcripts.
            </p>
            <a
              href="https://fnsnews.talk2keisha.com"
              target="_blank"
              rel="noopener noreferrer"
              className="app-link"
            >
              Go to FNS
            </a>
          </div>
          <div className="app-card">
            <h4>Fragile News Decoder</h4>
            <p>
              This is an interactive AI ethics tool designed to analyze any
              piece of news or media for hidden racial bias and white fragility.
              It returns a detailed report highlighting omissions, biases, and a
              "fragility score."
            </p>
            <Link to="/bias-detection" className="app-link">
              Go to Fragile News Decoder
            </Link>
          </div>
        </div>
      </section>

      <section>
        <h2>
          3. Prior Work: White Fragility in LLMs & the Case for Counter-Racist
          AI
        </h2>
        <p>
          My prior research revealed that state-of-the-art LLMs frequently omit
          or downplay the role of white supremacy in answers about racism,
          reflecting a dangerous bias toward comfort over truth. This "omission
          bias" is a digital reflection of white supremacist influence. In
          "Exploring White Fragility in Large Language Models" (IJCSIT, 2024), I
          showed that frontier LLMs frequently omit "white supremacy" when
          defining racism and only acknowledge it after pressure—an omission
          that functionally protects dominant-group narratives. These studies
          motivate a specialized stack that defaults to clarity about power.
        </p>
        <p>
          In the current era, white supremacist ideology has merely repackaged
          itself for the 21st century. In 2023 alone, the Anti-Defamation League
          (ADL) documented 7,567 incidents of white supremacist propaganda
          across the United States—a 12% increase over the prior year. Donald
          Trump's own equivocal responses to white nationalist incidents have
          emboldened white supremacists who see the MAGA movement as sympathetic
          to their cause.
        </p>
      </section>

      <section>
        <h2>4. Trump's Controversies: From Felony Charges to Maxwell</h2>
        <p>
          Donald Trump's personal and legal troubles underscore the paradox at
          the heart of the MAGA crusade against "wokeness". In 2024, Trump
          became the first former U.S. president to be convicted on 34 felony
          counts related to a hush-money cover-up scheme. This did little to
          dent Trump's standing among his supporters, a testament to the tribal
          loyalty and the continued success of the MAGA movement, which by this
          time had returned Trump to the White House despite his unprecedented
          legal baggage.
        </p>
        <p>
          Another controversy illustrates the extremes of this tolerance. Trump
          had long-standing social ties to Jeffrey Epstein and Ghislaine
          Maxwell. In 2025, a startling sequence of events unfolded: Maxwell was
          given apparent preferential treatment by Trump's Justice Department.
          Trump's Deputy Attorney General conducted an unusual closed-door
          interview of Maxwell in July 2025, and days later she was abruptly
          transferred to a far more comfortable minimum-security prison camp.
          The episode highlighted what many see as rank hypocrisy, with a
          self-styled crusader against "woke" corruption possibly protecting one
          of the most notorious criminals.
        </p>
      </section>

      <section>
        <h2>5. Conclusion</h2>
        <p>
          As this deepening saga shows, neutrality in the face of injustice is
          not neutrality at all – it is complicity. The Keisha News Network,
          driven by soWSnewsletter and expressed through MAGA Files and FNS News
          Decoded, shows how to build scalable, ethical AI that is
          evidence-anchored, audit-friendly, and unflinching. This is Woke AI in
          its precise sense: an AI that stays awake to history, names supremacy
          when present, and supplies the missing context—in production, at
          speed.
        </p>
      </section>

      <section>
        <h2>Full Research Paper</h2>
        <p>
          For the complete academic paper with detailed methodology, analysis,
          and citations, please access the full research document:
        </p>
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <a
            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5463340"
            target="_blank"
            rel="noopener noreferrer"
            className="app-link"
            style={{ fontSize: "1.1rem", padding: "15px 25px" }}
          >
            Read Full Research Paper - "Woke AI vs. MAGA: Scalable & Ethical AI
            in Action with Keisha AI"
          </a>
        </div>
      </section>
    </div>
  );
};

export default KnnIntroPage;
